/**
 * BCE Branche Full Module
 *
 * Full-width flip-card rebuild of the branchenfinder. Two data sources,
 * auto-detected per container:
 *
 *   1. JSON mode  — a <script type="application/json"> inside the container
 *      holds the categories. The grid is rendered from scratch (no slider,
 *      no cloning). This is the maintainable path: edit the JSON in TYPO3.
 *
 *   2. Transform mode — no JSON present: the module hijacks the existing
 *      TYPO3 branchenfinder (Tiny Slider), strips the slider and rebuilds
 *      each category as a flip card from the rendered markup.
 *
 * Both paths produce identical DOM: a `.branch_finder-categories__list` grid
 * of `.category-item > .flip-card-inner > .flip-card-front + .flip-card-back`.
 *
 * JSON shape:
 *   [
 *     {
 *       "title": "Automotive",
 *       "icon": "https://…/automotive.svg",   // optional image/SVG URL
 *       "links": [ { "label": "Batterietest", "href": "/de-de/…" } ]
 *     }
 *   ]
 *
 * @class BceBrancheFull
 */

// Where the branchenfinder icons are served from (see assets/icons/branche/).
// Overridable per container via data-icon-base for local testing.
const DEFAULT_ICON_BASE = 'https://bce.binder-world.com/icons/branche';

class BceBrancheFull {
    constructor() {
        this.instances = new WeakMap();
        this.autoInit = false;
    }

    /**
     * Initialize all branche-full containers in the DOM.
     * @param {string} selector
     */
    init(selector = '.bce_branche-full') {
        document.querySelectorAll(selector).forEach((el) => {
            if (!this.instances.has(el)) {
                this.create(el);
            }
        });
    }

    /**
     * Build the flip-card grid for a single container.
     * @param {HTMLElement} root - element with the bce_branche-full class
     * @returns {Object|null} instance
     */
    create(root) {
        if (this.instances.has(root)) {
            return this.instances.get(root);
        }

        const metaScript = root.querySelector('script[data-branche-meta]');
        const plainJson = root.querySelector(
            'script[type="application/json"]:not([data-branche-meta]):not([data-crosstree-i18n])'
        );
        let observer = null;

        if (metaScript) {
            // Translatable path: structural meta + a cross-tree-translator i18n block
            this.renderFromMeta(root, metaScript);
        } else if (plainJson) {
            // Simple path: a single JSON array, not translated
            this.renderFromJson(root, plainJson);
        } else if (root.querySelector('ul')) {
            // Editor path: an authored ul>li tree (TYPO3 resolves the links)
            this.transformFromList(root);
        } else {
            // Fallback: rebuild the legacy TYPO3 branchenfinder slider markup
            observer = this.transformExisting(root);
        }

        // Flip interaction works for both modes
        this.attachTouchToggle(root);

        // Reveal now that the grid is built (see the .bce-ready CSS)
        root.classList.add('bce-ready');

        const instance = {
            root,
            observer,
            destroy: () => {
                if (observer) observer.disconnect();
                this.instances.delete(root);
            }
        };

        this.instances.set(root, instance);
        return instance;
    }

    // ===== JSON MODE =====

    /**
     * Translatable path. Structural data (icon short-name, link hrefs) lives in
     * a `data-branche-meta` script; translatable strings (title, link labels)
     * live in a sibling `data-crosstree-i18n` script that the cross-tree
     * translator localizes. Both arrays are zipped by index — same shape as the
     * proven product-overview pattern.
     *
     *   meta[i]  = { "icon": "pharma", "links": ["/de-de/a", "/de-de/b"] }
     *   i18n[i]  = { "title": "Pharma", "links": ["Label A", "Label B"] }
     *
     * @param {HTMLElement} root
     * @param {HTMLScriptElement} metaScript
     */
    renderFromMeta(root, metaScript) {
        const meta = this.parseJson(metaScript, 'meta');
        if (!Array.isArray(meta) || meta.length === 0) {
            console.warn('BCE branche-full: data-branche-meta must be a non-empty array');
            return;
        }

        const i18nScript = root.querySelector('[data-crosstree-i18n]');
        const i18n = i18nScript ? this.parseJson(i18nScript, 'i18n') || [] : [];

        const categories = meta.map((m, i) => {
            const t = i18n[i] || {};
            // Direct-link tile: a single "link" (whole tile navigates, no flip)
            if (m.link) {
                return { icon: m.icon, title: t.title || '', link: m.link };
            }
            const labels = t.links || [];
            const links = (m.links || []).map((href, j) => ({
                href,
                label: labels[j] || ''
            }));
            return { icon: m.icon, title: t.title || '', links };
        });

        this.renderGrid(root, categories, [metaScript, i18nScript]);
    }

    /**
     * Simple path: a single JSON array with everything inline (not translated).
     *   [ { "icon": "pharma", "title": "Pharma",
     *       "links": [ { "href": "/…", "label": "…" } ] } ]
     * @param {HTMLElement} root
     * @param {HTMLScriptElement} jsonScript
     */
    renderFromJson(root, jsonScript) {
        const categories = this.parseJson(jsonScript, 'JSON');
        if (!Array.isArray(categories) || categories.length === 0) {
            console.warn('BCE branche-full: JSON must be a non-empty array');
            return;
        }
        this.renderGrid(root, categories, [jsonScript]);
    }

    /**
     * Build and insert the flip-card grid from normalized category data.
     * @param {HTMLElement} root
     * @param {Array<{icon:string,title:string,links:Array<{href:string,label:string}>}>} categories
     * @param {Array<HTMLElement|null>} sourceScripts - JSON scripts to remove after render
     */
    renderGrid(root, categories, sourceScripts) {
        const iconBase = root.dataset.iconBase || DEFAULT_ICON_BASE;

        const list = document.createElement('div');
        list.className = 'branch_finder-categories__list';

        categories.forEach((cat) => {
            const makeIcon = () => this.createIconNode(cat.icon, cat.title, iconBase);
            const makeTitle = () => this.createTitleNode(cat.title);
            const item = document.createElement('div');

            if (cat.link) {
                // Direct-link tile (no flip)
                item.className = 'category-item category-item--link';
                item.appendChild(this.buildLinkTile({ makeIcon, makeTitle, href: cat.link }));
            } else {
                const linkNodes = (cat.links || [])
                    .filter((link) => link && link.href)
                    .map((link) => {
                        const a = document.createElement('a');
                        a.href = link.href;
                        a.textContent = link.label || link.href;
                        this.applyLinkTarget(a);
                        return a;
                    });
                item.className = 'category-item';
                item.appendChild(this.buildInner({ makeIcon, makeTitle, linkNodes }));
            }

            list.appendChild(item);
        });

        sourceScripts.forEach((el) => el && el.remove());
        root.appendChild(list);
    }

    /**
     * Resolve an icon reference to an <img>. A short name ("pharma") maps to
     * `${iconBase}/pharma.svg`; a value containing "/" or ending in ".svg" with
     * a path is used verbatim.
     * @param {string} icon
     * @param {string} title - used as alt text
     * @param {string} iconBase
     * @returns {HTMLImageElement|null}
     */
    createIconNode(icon, title, iconBase) {
        if (!icon) return null;
        let src;
        if (icon.includes('/')) {
            src = icon; // full URL or absolute path
        } else {
            src = `${iconBase}/${icon.endsWith('.svg') ? icon : `${icon}.svg`}`;
        }
        const img = document.createElement('img');
        img.className = 'category-icon';
        img.src = src;
        img.alt = title || '';
        img.loading = 'lazy';
        return img;
    }

    /**
     * @param {HTMLElement} script
     * @param {string} label - for the warning message
     * @returns {*} parsed value or null
     */
    parseJson(script, label) {
        try {
            return JSON.parse(script.textContent.trim());
        } catch (error) {
            console.warn(`BCE branche-full: invalid ${label} JSON`, error);
            return null;
        }
    }

    // ===== LIST MODE (authored ul>li tree) =====

    /**
     * Transform an authored `ul > li` tree into the flip-card grid. Links are
     * already resolved by TYPO3 (the editor built them in a text/HTML element),
     * so no hardcoding or client-side ID lookup is needed.
     *
     *   <div class="bce_branche-full">
     *     <ul>
     *       <li>pharma-industry :: Pharma          ← category (flips)
     *         <ul>
     *           <li><a href="…">Zellkultur</a></li>
     *           <li><a href="…">ULT-Lagerung</a></li>
     *         </ul>
     *       </li>
     *       <li><a href="…">defence :: Defence</a></li>   ← direct-link tile (no flip)
     *     </ul>
     *   </div>
     *
     * The `icon-name ::` prefix on a title selects the BCE-served icon and is
     * stripped from the visible text.
     *
     * @param {HTMLElement} root
     */
    transformFromList(root) {
        const topList = root.querySelector('ul');
        if (!topList) return;

        const iconBase = root.dataset.iconBase || DEFAULT_ICON_BASE;
        const items = Array.from(topList.children).filter((el) => el.tagName === 'LI');

        const grid = document.createElement('div');
        grid.className = 'branch_finder-categories__list';

        items.forEach((li) => {
            const subList = li.querySelector(':scope > ul');
            const item = document.createElement('div');

            if (!subList) {
                // Direct-link tile: the whole li is a single link, no back side
                const anchor = li.querySelector('a');
                if (!anchor) return;
                const { icon, title } = this.parseIconToken(anchor.textContent);
                const makeIcon = () => this.createIconNode(icon, title, iconBase);
                const makeTitle = () => this.createTitleNode(title);
                item.className = 'category-item category-item--link';
                item.appendChild(this.buildLinkTile({ makeIcon, makeTitle, href: anchor.getAttribute('href') }));
            } else {
                // Flip category: heading text + nested links
                const { icon, title } = this.parseIconToken(this.headingText(li, subList));
                const makeIcon = () => this.createIconNode(icon, title, iconBase);
                const makeTitle = () => this.createTitleNode(title);
                const linkNodes = Array.from(subList.querySelectorAll('a')).map((a) => {
                    const clone = a.cloneNode(true);
                    clone.className = '';
                    this.applyLinkTarget(clone);
                    return clone;
                });
                item.className = 'category-item';
                item.appendChild(this.buildInner({ makeIcon, makeTitle, linkNodes }));
            }

            grid.appendChild(item);
        });

        topList.parentElement.replaceChild(grid, topList);
    }

    /**
     * Text of an <li> excluding its nested sub-list.
     * @param {HTMLElement} li
     * @param {HTMLElement} subList
     * @returns {string}
     */
    headingText(li, subList) {
        const clone = li.cloneNode(true);
        clone.querySelectorAll('ul').forEach((u) => u.remove());
        return clone.textContent.trim();
    }

    /**
     * Split an "icon-name :: Title" prefix off a heading. No prefix -> no icon.
     * @param {string} raw
     * @returns {{icon: string, title: string}}
     */
    parseIconToken(raw) {
        const text = (raw || '').replace(/\s+/g, ' ').trim();
        const m = text.match(/^([A-Za-z0-9_-]+)\s*::\s*(.+)$/);
        if (m) return { icon: m[1], title: m[2].trim() };
        return { icon: '', title: text };
    }

    /**
     * Open external links in a new tab, internal links in the same tab —
     * regardless of any target the RTE baked in.
     * @param {HTMLAnchorElement} a
     */
    applyLinkTarget(a) {
        const href = a.getAttribute('href') || '';
        let external = false;
        try {
            external = new URL(href, window.location.href).host !== window.location.host;
        } catch (e) {
            external = false;
        }
        if (external) {
            a.setAttribute('target', '_blank');
            a.setAttribute('rel', 'noopener noreferrer');
        } else {
            a.removeAttribute('target');
        }
    }

    /**
     * @param {string} title
     * @returns {HTMLElement|null}
     */
    createTitleNode(title) {
        if (!title) return null;
        const span = document.createElement('span');
        span.className = 'category-title';
        span.textContent = title;
        return span;
    }

    // ===== TRANSFORM MODE =====

    /**
     * Strip the Tiny Slider and rebuild the existing branchenfinder markup
     * into flip cards.
     * @param {HTMLElement} root
     * @returns {MutationObserver|null} observer guarding against slider re-init
     */
    transformExisting(root) {
        const observer = this.removeSlider(root);

        const categoryItems = root.querySelectorAll('.category-item');
        categoryItems.forEach((item) => {
            const button = item.querySelector('.btn');
            if (!button) return;

            const categoryId = button.getAttribute('data-category');
            const icon = button.querySelector('.category-icon');
            const title = button.querySelector('.category-title');
            const categoryWrap = categoryId ? root.querySelector(`#${categoryId}`) : null;
            if (!categoryWrap) return;

            const makeIcon = () => (icon ? icon.cloneNode(true) : null);
            const makeTitle = () => (title ? title.cloneNode(true) : null);

            const linkNodes = Array.from(categoryWrap.querySelectorAll('a')).map((link) => {
                const clone = link.cloneNode(true);
                clone.className = '';
                return clone;
            });

            item.innerHTML = '';
            item.appendChild(this.buildInner({ makeIcon, makeTitle, linkNodes }));
        });

        // Hide the original category wraps (source of the link data)
        root.querySelectorAll('.category-wrap').forEach((wrap) => {
            wrap.style.display = 'none';
        });

        // Remove the "show more" toggle
        const showMoreBtn = root.querySelector('.js-collapse-btn');
        if (showMoreBtn) {
            const container = showMoreBtn.closest('.text-center');
            (container || showMoreBtn).remove();
        }

        return observer;
    }

    /**
     * Unwrap the slider list from Tiny Slider and prevent re-initialization.
     * @param {HTMLElement} root
     * @returns {MutationObserver|null}
     */
    removeSlider(root) {
        const tnsOuter = root.querySelector('.tns-outer');
        const sliderList = root.querySelector('.branch_finder-categories__list');

        if (tnsOuter && sliderList) {
            // Drop cloned slides
            sliderList.querySelectorAll('.tns-slide-cloned').forEach((el) => el.remove());

            // Move the list out of the slider wrapper, then drop the wrapper
            tnsOuter.parentElement.insertBefore(sliderList, tnsOuter);
            tnsOuter.remove();

            this.stripTnsClasses(sliderList);
            sliderList.removeAttribute('style');
            sliderList.removeAttribute('id');

            sliderList.querySelectorAll('.category-item').forEach((item) => {
                this.stripTnsClasses(item);
                ['aria-hidden', 'tabindex', 'id', 'style'].forEach((attr) => item.removeAttribute(attr));
            });
        }

        const controls = root.querySelector('.tns-controls');
        if (controls) controls.remove();

        if (!sliderList) return null;

        // Guard: Tiny Slider may re-init after us and re-apply inline styles/classes
        const observer = new MutationObserver(() => {
            if (sliderList.style.transform || sliderList.style.height) {
                sliderList.removeAttribute('style');
            }
            if (sliderList.className.includes('tns-')) {
                this.stripTnsClasses(sliderList);
            }
        });

        observer.observe(sliderList, {
            attributes: true,
            attributeFilter: ['style', 'class']
        });

        return observer;
    }

    /**
     * Remove all `tns-*` classes from an element.
     * @param {HTMLElement} el
     */
    stripTnsClasses(el) {
        el.className = el.className
            .split(' ')
            .filter((cls) => !cls.startsWith('tns-'))
            .join(' ');
    }

    // ===== SHARED =====

    /**
     * Build the flip-card inner structure from icon/title factories and links.
     * The factories are called once per side so front and back get independent
     * nodes.
     * @param {Object} data
     * @param {Function} data.makeIcon  - returns a fresh icon node or null
     * @param {Function} data.makeTitle - returns a fresh title node or null
     * @param {HTMLElement[]} data.linkNodes - anchor nodes for the back side
     * @returns {HTMLElement} .flip-card-inner
     */
    buildInner({ makeIcon, makeTitle, linkNodes }) {
        const inner = document.createElement('div');
        inner.className = 'flip-card-inner';

        // Front side
        const front = document.createElement('div');
        front.className = 'flip-card-front';
        const btn = document.createElement('div');
        btn.className = 'btn';
        const frontIcon = makeIcon();
        const frontTitle = makeTitle();
        if (frontIcon) btn.appendChild(frontIcon);
        if (frontTitle) btn.appendChild(frontTitle);
        front.appendChild(btn);

        // Back side
        const back = document.createElement('div');
        back.className = 'flip-card-back';

        const backIcon = makeIcon();
        const backTitle = makeTitle();
        if (backIcon || backTitle) {
            const header = document.createElement('div');
            header.className = 'flip-back-header';
            if (backIcon) header.appendChild(backIcon);
            if (backTitle) header.appendChild(backTitle);
            back.appendChild(header);
        }

        if (linkNodes.length > 0) {
            const linksContainer = document.createElement('div');
            linksContainer.className = 'flip-back-links';
            linkNodes.forEach((link) => linksContainer.appendChild(link));
            back.appendChild(linksContainer);
        }

        inner.appendChild(front);
        inner.appendChild(back);
        return inner;
    }

    /**
     * Build a non-flipping tile whose whole face is a link (e.g. Defence).
     * @param {Object} data
     * @param {Function} data.makeIcon
     * @param {Function} data.makeTitle
     * @param {string} data.href
     * @returns {HTMLElement} .flip-card-inner with a single <a> front
     */
    buildLinkTile({ makeIcon, makeTitle, href }) {
        const inner = document.createElement('div');
        inner.className = 'flip-card-inner';

        const front = document.createElement('a');
        front.className = 'flip-card-front';
        if (href) {
            front.setAttribute('href', href);
            this.applyLinkTarget(front);
        }

        const btn = document.createElement('div');
        btn.className = 'btn';
        const icon = makeIcon();
        const title = makeTitle();
        if (icon) btn.appendChild(icon);
        if (title) btn.appendChild(title);
        front.appendChild(btn);

        inner.appendChild(front);
        return inner;
    }

    /**
     * On touch devices, tap flips the card (hover does not exist). Only one
     * card stays open at a time; taps on back-side links pass through.
     * @param {HTMLElement} root
     */
    attachTouchToggle(root) {
        const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
        if (!isTouchDevice) return;

        const items = root.querySelectorAll('.category-item');
        items.forEach((item) => {
            // Direct-link tiles navigate on tap; no flip toggle
            if (item.classList.contains('category-item--link')) return;
            item.addEventListener('click', function (e) {
                const isFlipped = this.classList.contains('is-flipped');

                // Let link taps on the flipped back side navigate
                if (e.target.tagName === 'A' && isFlipped) return;
                if (e.target.tagName !== 'A') e.preventDefault();

                items.forEach((other) => {
                    if (other !== this) other.classList.remove('is-flipped');
                });
                this.classList.toggle('is-flipped');
            });
        });
    }

    /**
     * @param {HTMLElement} root
     * @returns {Object|null}
     */
    getInstance(root) {
        return this.instances.get(root) || null;
    }
}

export default new BceBrancheFull();
